import { Fragment, useMemo } from "react";
import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { formatPercentage } from "score-common";
import { useTranslation } from "i18n-fe";
import { useRidesForClass } from "../hooks/useRidesForClass";
import {
  buildClassStandings,
  groupStandingsByDivision,
  standingsJudgeColumns,
} from "../util/classStandings";

// Phone-width first: a scribe's tablet or phone, three or four judge columns
// plus the composite, so cell padding is tight and the percentages never wrap.
const COMPACT_TABLE_SX = {
  "& .MuiTableCell-root": { px: 0.75, py: 0.75, fontSize: "0.8125rem" },
  "& td:not(:nth-of-type(2)), & th:not(:nth-of-type(2))": {
    whiteSpace: "nowrap",
  },
};

// #3371 - at 5 judges (place + rider + 5 + composite) the table is wider than a
// phone, so the wrapper scrolls sideways. Keep Place and Rider pinned left so a
// row stays identifiable while the judge columns scroll under them.
const PLACE_COL_WIDTH = 40;
const STANDINGS_TABLE_SX = {
  ...COMPACT_TABLE_SX,
  "& th:nth-of-type(1), & td:nth-of-type(1)": {
    position: "sticky",
    left: 0,
    zIndex: 1,
    bgcolor: "background.paper",
    minWidth: PLACE_COL_WIDTH,
    maxWidth: PLACE_COL_WIDTH,
  },
  "& th:nth-of-type(2), & td:nth-of-type(2)": {
    position: "sticky",
    left: PLACE_COL_WIDTH,
    zIndex: 1,
    bgcolor: "background.paper",
    minWidth: 110,
  },
  "& th:nth-of-type(n+3), & td:nth-of-type(n+3)": { minWidth: 52 },
};

// #2859 - the expanded body of a PLACED class card on the class list: final
// standings by placing, each judge's percentage, and the composite. Mounted
// only while the card is expanded, so the per-class rides + score sheets
// fetch (useRidesForClass, cache-first, same hook the ride list uses) never
// runs for a collapsed card.
//
// #3380 / #3406 - when the class is Placed by division (its own
// class.placedByDivision setting, the one rule score-api and the scoreboard
// obey), the rows split into one headed group per rider-status
// division. Otherwise one unheaded list.
export default function ClassStandings({ classId, placedByDivision }) {
  const { t } = useTranslation("score");
  const { data, settled } = useRidesForClass(classId);
  const rows = useMemo(() => buildClassStandings(data?.rides), [data]);
  const judges = useMemo(() => standingsJudgeColumns(rows), [rows]);
  const groupByDivision = placedByDivision === true;
  const groups = useMemo(
    () =>
      groupByDivision
        ? groupStandingsByDivision(rows)
        : [{ key: "all", label: null, rows }],
    [groupByDivision, rows],
  );

  if (!settled && !data) {
    return (
      <Box
        sx={{ px: 2, pb: 2 }}
        data-testid="class-standings-loading"
        aria-label={t("classes.standings.loading")}
      >
        <Skeleton variant="rounded" height={72} />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Typography
        variant="body2"
        sx={{ px: 2, pb: 2, color: "text.secondary" }}
      >
        {t("classes.standings.empty")}
      </Typography>
    );
  }

  return (
    <Box sx={{ px: 1, pb: 2, overflowX: "auto" }} data-testid="class-standings">
      <Table
        size="small"
        aria-label={t("classes.standings.title")}
        sx={STANDINGS_TABLE_SX}
      >
        <TableHead>
          <TableRow>
            <TableCell>{t("classes.standings.place")}</TableCell>
            <TableCell>{t("classes.standings.rider")}</TableCell>
            {judges.map((j) => (
              <TableCell
                key={j}
                align="right"
                data-testid={`standings-judge-${j}`}
                aria-label={t("classes.standings.judge", { position: j })}
              >
                {j}
              </TableCell>
            ))}
            <TableCell align="right">
              {t("classes.standings.composite")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <Fragment key={group.key}>
              {groupByDivision && (
                <TableRow data-testid="standings-division-row">
                  <TableCell
                    colSpan={judges.length + 3}
                    sx={{ fontWeight: 700, bgcolor: "action.hover" }}
                    data-testid="standings-division"
                  >
                    {group.label ?? t("classes.standings.open")}
                  </TableCell>
                </TableRow>
              )}
              {group.rows.map((row) => (
                <TableRow key={row.rideId} data-testid="standings-row">
                  <TableCell data-testid="standings-place">
                    {row.placing > 0
                      ? row.placing
                      : t("classes.standings.noRibbon")}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {row.riderName || "-"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {row.horseName}
                    </Typography>
                  </TableCell>
                  {judges.map((j) => {
                    const js = row.judgeScores.find((s) => s.judgeId === j);
                    return (
                      <TableCell
                        key={j}
                        align="right"
                        data-testid={`standings-score-${j}`}
                      >
                        {formatPercentage(js?.percentage)}
                      </TableCell>
                    );
                  })}
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700 }}
                    data-testid="standings-composite"
                  >
                    {formatPercentage(row.composite)}
                  </TableCell>
                </TableRow>
              ))}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
